import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Modal, Input, Button, message, Spin } from "antd";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import styled from "styled-components";
import "./style.css";
import { CustomDropDown } from "components/customDropDown";
import { createApiClient } from "apiClient";
import type { InputRef } from "antd";

interface IWeightThreshold {
  upper_threshold: string;
  lower_threshold: string;
  patient_internal_id: number;
  unit: string;
}

const SetWeightThresholdModal: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const [dailyChecked, setDailyChecked] = useState(true);
  const [loading, setLoading] = useState(false);

  const [weightThreshold, setWeightThreshold] = useState<IWeightThreshold>({
    upper_threshold: "",
    lower_threshold: "",
    patient_internal_id: Number(sessionStorage.getItem("patientId")) || 0,
    unit: "kg"
  });

  // Add refs for Max and Min input fields
  const maxWeightRef = useRef<InputRef>(null);
  const minWeightRef = useRef<InputRef>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setWeightThreshold(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    if (maxWeightRef.current) {
      maxWeightRef.current.focus();
    }
  }, [weightThreshold.upper_threshold]);

  useEffect(() => {
    if (minWeightRef.current) {
      minWeightRef.current.focus();
    }
  }, [weightThreshold.lower_threshold]);

  const handleCancel = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleUpdate = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
  };

  const CustomStyledModal = styled.div`
    .ant-modal-header {
      border-radius: 10px;
    }
    .ant-modal-content {
      border-radius: 10px;
      font-weight: bold;
    }
    .ant-modal-footer {
      display: flex;
      justify-content: flex-start;
    }
    .ant-modal-title {
      font-weight: 800;
      font-size: 14px;
    }
  `;

  const dropDownValues = ["kg", "lbs"];

  const setSelectedValue = (value: string) => {
    setWeightThreshold(prev => ({
      ...prev,
      unit: value
    }));
  };

  // Update handler: read values from state and send to API
  const handleUpdateWeight = async () => {
    const body = {
      lower_threshold: weightThreshold.lower_threshold,
      upper_threshold: weightThreshold.upper_threshold,
      patient_internal_id: weightThreshold.patient_internal_id,
      unit: weightThreshold.unit
    };

    try {
      setLoading(true);
      const threshold_data = await createApiClient().postWeightThresholdData(
        body
      );
      if (threshold_data) {
        message.success("Updated successfully", 2);
      } else {
        message.error("Every Threshold point is required");
      }
    } catch {
      message.error("Every Threshold point is required");
    } finally {
      setLoading(false);
      setIsModalVisible(false);
      setIsEditMode(false);
    }
  };

  // Fetch weight threshold data and populate inputs
  const handleGetWeightThreshold = async (patientId: any) => {
    try {
      setLoading(true);
      const threshold_data = await createApiClient().getWeightThresholdData(
        patientId
      );
      if (threshold_data) {
        setWeightThreshold({
          upper_threshold: threshold_data.upper_threshold || "",
          lower_threshold: threshold_data.lower_threshold || "",
          patient_internal_id: patientId,
          unit: threshold_data.unit || "kg"
        });
      }
    } catch (error) {
      console.log("Error", error);
    } finally {
      setLoading(false);
    }
  };

  useLayoutEffect(() => {
    if (isModalVisible) {
      const fetchData = async () => {
        const patientId = sessionStorage?.patientId;
        await handleGetWeightThreshold(patientId);
      };
      fetchData();
    }
  }, [isModalVisible]);

  return (
    <div>
      <AppButton
        type={ButtonType.Skeleton}
        buttonContent="Set Threshold"
        style={{
          minWidth: 120,
          right: "15px",
          border: "1px solid black",
          marginTop: "-9px",
          color: "black"
        }}
        onClick={showModal}
      />
      <Modal
        title="Set Weight Threshold"
        open={isModalVisible}
        onCancel={handleCancel}
        width={540}
        footer={[
          <Button
            key="edit"
            type="primary"
            onClick={handleEdit}
            disabled={isEditMode}
            style={{
              background: "#2196F3",
              borderColor: "#2196F3",
              minWidth: 80
            }}
          >
            Edit
          </Button>,
          <Button
            key="cancel"
            type="default"
            onClick={handleCancel}
            style={{ minWidth: 80 }}
          >
            Cancel
          </Button>,
          <Button
            key="update"
            type="default"
            onClick={handleUpdateWeight}
            disabled={!isEditMode}
            style={{ minWidth: 80 }}
          >
            Update
          </Button>
        ]}
        style={{ fontWeight: "bold" }}
        modalRender={modal => <CustomStyledModal>{modal}</CustomStyledModal>}
      >
        {loading ? (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "centre",
              justifyContent: "center"
            }}
          >
            <Spin />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {/* Daily */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <CustomDropDown
                  title={"Weight Unit"}
                  dropDownValues={dropDownValues}
                  setSelectedValue={setSelectedValue}
                  selectedValue={weightThreshold.unit}
                />
              </div>
              <div
                style={{
                  marginTop: 22,
                  marginLeft: 40,
                  display: "flex",
                  gap: 18
                }}
              >
                <Input
                  ref={maxWeightRef}
                  name="upper_threshold"
                  value={weightThreshold.upper_threshold}
                  onChange={handleChange}
                  placeholder="Max."
                  style={{ width: 120, height: 35, borderRadius: 5 }}
                  disabled={!isEditMode || !dailyChecked}
                />
                <Input
                  ref={minWeightRef}
                  name="lower_threshold"
                  value={weightThreshold.lower_threshold}
                  onChange={handleChange}
                  placeholder="Min."
                  style={{ width: 120, height: 35, borderRadius: 5 }}
                  disabled={!isEditMode || !dailyChecked}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SetWeightThresholdModal;
